import { useMemo } from 'react';
import { useProducts } from '@/contexts/ProductsContext';
import { useSiteContentValue } from '@/hooks/useSiteContentValue';
import type { Product } from '@/types/product';

// The new design's "Shop by category" and "Shop by concern" circles, used by
// the Home shop, the header mega menu and the phone drawer.
// - The circles themselves (name per language, photo, order, hidden) are
//   edited in Admin > Website > Home Page > Shop categories & concerns and
//   stored as JSON in rxk_site_content ('shop.v2.config', locale 'en').
// - Which circles a product appears under is set on the product itself
//   (Admin > Products > product > Shop circles, rxk_product_content.design_v2
//   shopCategories / shopConcerns), so it scales to any number of products.
//   A product that was never set falls back to its routine step and skin types.

export const SHOP_CONFIG_KEY = 'shop.v2.config';
export const SET_SLUG = '3-step-set';

export type Lang = 'en' | 'bn' | 'ko';
export type Texts = Partial<Record<Lang, string>>;

export interface ShopEntry {
  key: string;
  label: Texts;
  // Concerns only: the longer name on the filter chip and in the mega menu.
  long?: Texts;
  // Empty = the first product's photo.
  image: string;
  hidden?: boolean;
}

export interface ShopConfig {
  // The 'All' circle's photo; empty = the set photo.
  allImage?: string;
  categories: ShopEntry[];
  concerns: ShopEntry[];
}

export type ShopKind = 'categories' | 'concerns';

const STEP_CATEGORY: Record<string, string> = { SET: 'set', OPEN: 'essence', TREAT: 'serum', LOCK: 'cream' };
const SKIN_CONCERN: Record<string, string[]> = { 'acne-prone': ['spots'], dry: ['dry'], oily: ['oily'], combination: ['oily'], sensitive: ['sens'] };

const DEFAULT_CONFIG: ShopConfig = {
  categories: [
    { key: 'set', label: { en: 'Sets', bn: 'সেট' }, image: '' },
    { key: 'essence', label: { en: 'Toner & Essence', bn: 'টোনার ও এসেন্স' }, image: '/v2/p/booster-hero.webp' },
    { key: 'serum', label: { en: 'Serums', bn: 'সিরাম' }, image: '/v2/p/serum-hero.webp' },
    { key: 'cream', label: { en: 'Moisturisers', bn: 'ময়েশ্চারাইজার' }, image: '/v2/p/cream-hero.webp' },
  ],
  concerns: [
    { key: 'dull', label: { en: 'Dull skin', bn: 'নিষ্প্রভ ত্বক' }, long: { en: 'Dull & tired', bn: 'নিষ্প্রভ ও ক্লান্ত' }, image: '/v2/concern-dull.jpg' },
    { key: 'spots', label: { en: 'Dark spots', bn: 'কালো দাগ' }, long: { en: 'Dark spots & marks', bn: 'কালো দাগ ও ছোপ' }, image: '/v2/serum-c1.jpg' },
    { key: 'dry', label: { en: 'Dry skin', bn: 'শুষ্ক ত্বক' }, long: { en: 'Dry & tight', bn: 'শুষ্ক ও টানটান' }, image: '/v2/p/cream-c1.jpg' },
    { key: 'oily', label: { en: 'Oily skin', bn: 'তৈলাক্ত ত্বক' }, long: { en: 'Oily & pores', bn: 'তৈলাক্ত ও বড় লোমকূপ' }, image: '/v2/skin-oily.jpg' },
    { key: 'sens', label: { en: 'Sensitive', bn: 'সংবেদনশীল' }, long: { en: 'Sensitive', bn: 'সংবেদনশীল' }, image: '/v2/skin-dry.jpg' },
  ],
};
export const defaultShopConfig = (): ShopConfig => JSON.parse(JSON.stringify(DEFAULT_CONFIG));

// The circles a product shows under, from its own setting or the fallback.
export function shopKeysOf(p: { stepKeyword: string; skinTypes: string[]; designV2?: { shopCategories?: string[]; shopConcerns?: string[] } }, kind: ShopKind): string[] {
  if (kind === 'categories') return p.designV2?.shopCategories ?? (STEP_CATEGORY[p.stepKeyword] ? [STEP_CATEGORY[p.stepKeyword]] : []);
  if (p.designV2?.shopConcerns) return p.designV2.shopConcerns;
  if (p.stepKeyword === 'SET') return DEFAULT_CONFIG.concerns.map((c) => c.key);
  return Array.from(new Set(p.skinTypes.flatMap((s) => SKIN_CONCERN[s.toLowerCase()] ?? [])));
}

export function parseShopConfig(raw: string): ShopConfig | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as ShopConfig;
    return Array.isArray(v.categories) && Array.isArray(v.concerns) ? v : null;
  } catch {
    return null;
  }
}

export const textOf = (t: Texts | undefined, lang: string) => (t ? t[lang as Lang] || t.en || '' : '');

export interface ShopMember {
  slug: string;
  sku: string;
  name: string;
  image: string;
  categories: string[];
  concerns: string[];
}

// Every storefront product (and the set) with the circles it shows under.
export function useShopMembers(): ShopMember[] {
  const { products, routineSet } = useProducts();
  return useMemo(
    () => [
      ...(routineSet
        ? [
            {
              slug: SET_SLUG,
              sku: routineSet.sku,
              name: routineSet.designV2?.name || routineSet.name,
              image: routineSet.image,
              categories: shopKeysOf({ stepKeyword: 'SET', skinTypes: [], designV2: routineSet.designV2 }, 'categories'),
              concerns: shopKeysOf({ stepKeyword: 'SET', skinTypes: [], designV2: routineSet.designV2 }, 'concerns'),
            },
          ]
        : []),
      ...products.map((p: Product) => ({
        slug: p.id,
        sku: p.sku,
        name: p.designV2?.name || p.name,
        image: p.image,
        categories: shopKeysOf(p, 'categories'),
        concerns: shopKeysOf(p, 'concerns'),
      })),
    ],
    [products, routineSet],
  );
}

// The live circles: the saved config, or the defaults until one is saved.
export function useShopConfig(): ShopConfig {
  const raw = useSiteContentValue(SHOP_CONFIG_KEY, 'en', '');
  return useMemo(() => parseShopConfig(raw) ?? defaultShopConfig(), [raw]);
}
